<template>
    <div class="px-4 sm:px-8 lg:px-0 bg-base sm:bg-transparent">
        <div class="mx-auto my-12 w-full max-w-2xl bg-white rounded-lg shadow-md pt-12 pb-8 px-4 sm:px-12 text-main">
            <!-- ステップインジケーター (現在ステップ=2) -->
            <div class="flex justify-center mb-8">
                <div class="flex items-center">
                    <div class="flex flex-col items-center gap-0.5 w-16">
                        <i class="fa-solid fa-circle-check text-xl text-gray-300"></i>
                        <div class="text-[10px] text-sub text-center leading-3">
                            <p>Google</p>
                            <p>連携</p>
                        </div>
                    </div>
                    <div class="w-8 border-t border-gray-300"></div>
                    <div class="flex flex-col items-center gap-0.5 w-16">
                        <div class="w-5 h-5 rounded-full bg-emerald-400 flex items-center justify-center">
                            <span class="text-[10px] font-bold text-white leading-3">2</span>
                        </div>
                        <div class="text-[10px] font-bold text-sub text-center leading-3">
                            <p>アカウント</p>
                            <p>作成</p>
                        </div>
                    </div>
                    <div class="w-8 border-t border-gray-300"></div>
                    <div class="flex flex-col items-center gap-0.5 w-16">
                        <div class="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                            <span class="text-[10px] font-bold text-white leading-3">3</span>
                        </div>
                        <div class="text-[10px] text-sub text-center leading-3">
                            <p>チャンネル</p>
                            <p>申請</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- タイトル + 説明 -->
            <div class="flex flex-col gap-4 text-center mb-8">
                <h1 class="font-bold text-2xl leading-8">アカウントの作成</h1>
                <p class="text-sm leading-5">連携したGoogleアカウントをもとに、ECHOESアカウントを作成します。</p>
            </div>

            <!-- エラーメッセージ -->
            <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

            <!-- ガイドライン・規約 -->
            <div class="text-sm leading-5 mb-6">
                ご登録前には当サービスの<a href="/guideline" target="_blank" class="text-blue-600">「ガイドライン」</a>および、当社の<a href="https://www.livedoor.com/rules/" target="_blank" class="text-blue-600">「利用規約」</a>、<a href="https://www.livedoor.com/privacy/" target="_blank" class="text-blue-600">「プライバシーポリシー」</a>を必ずご確認頂き、同意の上、お申込ください。
            </div>

            <!-- 同意チェック -->
            <div class="mb-6">
                <label for="agreement" class="flex items-center cursor-pointer space-x-1 p-4 border rounded bg-base border-gray-100">
                    <input type="checkbox" id="agreement" v-model="isAgreed" class="sr-only" />
                    <i class="fa-solid fa-square-check text-xl transition-colors duration-200" :class="isAgreed ? 'text-emerald-800' : 'text-gray-200'"></i>
                    <span class="text-left text-[16px] leading-6 font-light">上記の内容を確認し、同意します</span>
                </label>
            </div>

            <button @click="createAccount" class="w-full text-center btn-lg" type="button" :disabled="!isAgreed || isSaving" :class="[isAgreed && !isSaving ? 'btn-primary text-white' : 'disabled:btn-disabled disabled:btn-lg']">アカウントを作成する
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';
import ErrorMessage from './ErrorMessage';

const store = useStore();
const router = useRouter();
const isAgreed = ref(false);
const isSaving = ref(false);
const errorMessage = ref("");

// アカウント作成処理
async function createAccount() {
    if (!isAgreed.value || isSaving.value) {
        return; // 既に保存処理中、未同意の場合は何もしない
    }
    isSaving.value = true; // 保存処理を開始するときにボタンを無効化

    // APIリクエスト
    try {
        const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
        // #301: LoginPage で Vuex に保存したチャンネル情報から channel_id を抽出して送信。
        // 選択チャンネルが無いケース (招待経由のみ) でも null で送って良い。
        const channelData = store.state.channelData;
        const selectedYoutubeChannelId = channelData && channelData.channel_id
            ? channelData.channel_id
            : null;
        const payload = {
            email: store.state.email,
            token: store.state.token,
            guidelineup: process.env.VUE_APP_GUIDELINE_UPDATE,
            action: 'create',
            selected_youtube_channel_id: selectedYoutubeChannelId,
        };
        const response = await axios.post(`${apiEndpoint}/user-auth/`, payload, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        console.log('Account created successfully', response.data);
        await store.dispatch('login', {
            user_id: response.data.user_id,
            accessToken: response.data.accessToken,
            expiry: response.data.expiry,
            guideline: JSON.stringify(response.data.userConsent),
            // #301: CS審査フロー用のステータスを取り込む
            applicationStatus: response.data.applicationStatus,
            waitForInvitation: response.data.waitForInvitation,
            showApprovalAlert: response.data.showApprovalAlert,
            hasMembership: response.data.hasMembership,
        });
        await store.dispatch('clearregisterdata');
        router.push({
            name: 'AccountCreated'
        });
    } catch (error) {
        console.error('Error creating account', error);
        errorMessage.value = 'アカウントの作成に失敗しました。';
    } finally {
        isSaving.value = false; // 処理が完了したらボタンを再び有効化
    }
}
</script>
